import {
    Collapse,
    Grid,
    IconButton,
    SxProps,
    Theme,
    Tooltip,
    useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Observer } from "mobx-react-lite";
import React from "react";
import DownChevronSvg from "./Select/BaseSelectComponents/DownChevronSvg";
import InfoIcon from "@mui/icons-material/Info";
import { styleClsx, sxClsx } from "utils/styleClsx";

interface IAcxExpansion {
    expanded?: boolean;
    onExpandChange?: (expanded: boolean, name: string) => void;
    header: React.ReactNode;
    name?: string;
    body?: React.ReactNode;
    rootStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    disableTooltip?: boolean;
    unmountOnExit?: boolean;
    conversationVariant?: boolean;
    additionalInformation?: string;
    headerSx?: SxProps<Theme>;
    headerExpandedSx?: SxProps<Theme>;
    headerClosedSx?: SxProps<Theme>;
    chevronExpandedSx?: SxProps<Theme>;
    chevronCloseSx?: SxProps<Theme>;
}

export default function AcxExpansion(props: IAcxExpansion) {
    const [isExpanded, setIsExpanded] = React.useState(props.expanded);
    const theme = useTheme();

    React.useEffect(() => {
        setIsExpanded(props.expanded);
    }, [props.expanded]);

    const expand = () => {
        if (props.onExpandChange) {
            props.onExpandChange(!isExpanded, props.name ? props.name : "");
        }
        setIsExpanded(!isExpanded);
    };

    const expandIcon = (
        <Grid item>
            {isExpanded ? (
                <Tooltip
                    disableFocusListener
                    title={props.disableTooltip ? "" : "Collapse"}
                    placement={"top-start"}
                >
                    <IconButton
                        data-testid="acx-expansion-button"
                        size="small"
                        onClick={expand}
                        sx={sxClsx(
                            props.chevronExpandedSx
                                ? props.chevronExpandedSx
                                : { color: theme.palette.white.contrastText },
                        )}
                    >
                        {props.conversationVariant ? (
                            <ExpandLessIcon fontSize="small" />
                        ) : (
                            <ExpandMoreIcon fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip
                    disableFocusListener
                    title={props.disableTooltip ? "" : "Expand"}
                    placement={"top-start"}
                >
                    <IconButton
                        data-testid="acx-expansion-button"
                        size="small"
                        onClick={expand}
                        color="primary"
                        sx={sxClsx(props.chevronCloseSx)}
                    >
                        {props.conversationVariant ? (
                            <DownChevronSvg />
                        ) : (
                            <ChevronRightIcon fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </Grid>
    );

    const headerDefaultStyle: SxProps<Theme> = {
        userSelect: "none",
        height: "24px",
        lineHeight: "24px",
        backgroundColor: theme.palette.white.main,
        color: theme.palette.white.contrastText,
        fontWeight: "bold",
    };

    return (
        <Observer>
            {() => (
                <div style={props.rootStyle}>
                    <Grid
                        container
                        wrap="nowrap"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={sxClsx(
                            props.headerSx ?? headerDefaultStyle,
                            isExpanded ? props.headerExpandedSx : {},
                            !isExpanded ? props.headerClosedSx : {},
                        )}
                    >
                        <Grid
                            container
                            item
                            alignItems="center"
                            alignContent={"flex-end"}
                            wrap="nowrap"
                        >
                            {!props.conversationVariant && expandIcon}
                            <Grid container item xs alignContent="center">
                                {props.header}
                                {!!props.additionalInformation && (
                                    <Tooltip
                                        title={props.additionalInformation}
                                        placement="right"
                                        style={{ marginLeft: 5 }}
                                    >
                                        <InfoIcon color="secondary" />
                                    </Tooltip>
                                )}
                            </Grid>
                            {props.conversationVariant && expandIcon}
                        </Grid>
                    </Grid>

                    <Collapse
                        in={isExpanded}
                        unmountOnExit={props.unmountOnExit}
                    >
                        <div
                            data-testid="acx-expansion-body"
                            style={styleClsx(
                                props.bodyStyle,
                                isExpanded && { display: "block" },
                                !isExpanded && { display: "none" },
                            )}
                        >
                            {props.body}
                        </div>
                    </Collapse>
                </div>
            )}
        </Observer>
    );
}
